import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage, useField } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import apiConfig from "../credentials/apiConfig";
import { Link, useNavigate, useLocation } from "react-router-dom";


Yup.addMethod(Yup.string, 'isFormattedNumber', function() {
  return this.test('is-formatted-number', 'Must be a valid number', function(value) {
    if (!value) return true;
    const numericValue = value.replace(/,/g, '');
    return !isNaN(numericValue) && isFinite(numericValue);
  });
});

const validationSchema = Yup.object({
    clientId: Yup.string().required("Client is required"),
    engagementType: Yup.string().required("Engagement Type is required"),
    engagementYear: Yup.string().required("Engagement Year is required"),
    materiality: Yup.string()
        .required("Materiality is required")
        .isFormattedNumber()
        .test(
            'is-positive',
            'Amount must be greater than zero',
            value => {
                if (!value) return true;
                const numericValue = parseFloat(value.replace(/,/g, ''));
                return numericValue > 0;
            }
        ),
});


const FormattedNumberInput = ({ ...props }) => {
    const [field, meta, helpers] = useField(props.name);
    const formatNumber = (value) => {
        if (!value) return '';
        const cleanValue = value.toString().replace(/[^\d.]/g, '');
        const parts = cleanValue.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.join('.');
    };

    const handleChange = (e) => {
        const input = e.target;
        const rawValue = input.value;
        const cursorPosition = input.selectionStart;
        const commasBefore = (rawValue.substring(0, cursorPosition).match(/,/g) || []).length;
        const cleanValue = rawValue.replace(/,/g, '');
        helpers.setValue(formatNumber(cleanValue));
        
        setTimeout(() => {
            const formattedValue = formatNumber(cleanValue);
            input.value = formattedValue;
            if (cursorPosition !== null) {
                const originalTextLength = rawValue.length;
                const newCursorPosition = cursorPosition + 
                    ((formattedValue.substring(0, cursorPosition).match(/,/g) || []).length - commasBefore);
                const finalPosition = cursorPosition === originalTextLength ? 
                    formattedValue.length : 
                    newCursorPosition;
                input.setSelectionRange(finalPosition, finalPosition);
            }
        }, 10);
    };

    return (
        <input
            {...props}
            value={field.value}
            onChange={handleChange}
            onBlur={field.onBlur}
            className="form-control p-3"
        />
    );
};

const EngagementModal = ({ show, handleClose, clientId, onToggle }) => {
    const [loading, setLoading] = useState(false);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [engagementResponse, setEngagementResponse] = useState(null);
    const [clients, setClients] = useState([]);
    const [engagementYears, setEngagementYears] = useState([]);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    
    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());
        setEngagementYears(years);
    }, []);
    
    useEffect(() => {
        if (show) {
            fetchClients();
        }
    }, [show]);

    const fetchClients = async () => {
        setClientsLoading(true);
        setError(null);
        try {
            const authToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${apiConfig.apiBaseURL}/getAllClients`,
                {
                    headers: {
                        "x-api-key": apiConfig["x-api-key"],
                        "Authorization": `Bearer ${authToken}`
                    }
                }
            );

            if (response.data && response.data.success) {
                setClients(response.data.clients);
            } else {
                setError(response.data.error);
                toast.error(response.data.error);
            }
        } catch (error) {
            console.error("Error fetching clients:", error);
            setError("Error fetching clients: " + (error.message || "Unknown error"));
            toast.error("Failed to load client list");
        } finally {
            setClientsLoading(false);
        }
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        const isValidObjectId = (id) => /^[0-9a-fA-F]{24}$/.test(id);
        if (!isValidObjectId(values.clientId)) {
            toast.error("Invalid Client ID format");
            setSubmitting(false);
            return;
        }

        try {
            setLoading(true);
            const authToken = localStorage.getItem("authToken");
            const selectedClient = clients.find(
                client => (client._id || client.clientId) === values.clientId
            );
            const clientName = selectedClient ? selectedClient.clientName : "";

            // Remove commas from materiality before sending to server
            const materialityValue = parseFloat(values.materiality.replace(/,/g, ''));

            const response = await axios.post(
                `${apiConfig.apiBaseURL}/createEngagement`,
                {
                    clientId: values.clientId,
                    clientName: clientName,
                    engagementType: values.engagementType,
                    engagementYear: values.engagementYear,
                    materiality: materialityValue,
                    expectations: values.expectations
                },
                {
                    headers: {
                        "x-api-key": apiConfig["x-api-key"],
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${authToken}`
                    }
                }
            );
            if (response.data.success) {
                setEngagementResponse(response.data.engagement);
                toast.success(response.data.message, { autoClose: 2000 });
                resetForm();
                handleClose();
                navigate(`/engagements/edit-engagement-setup/${response.data.engagement._id}`, { state: { engagementId: response.data.engagement._id }});
            } else {
                toast.error(response.data.message || "An error occurred while creating engagement");
            }
        } catch (error) {
            console.log("Error creating engagement:", error);
            toast.error(error.response?.data?.message || "An error occurred while creating engagement");
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const CustomSelect = ({ field, form, ...props }) => {
        return (
            <div className="position-relative">
                <select {...field} {...props} className="form-control p-3 pe-5" />
                <div style={{ 
                    position: 'absolute', 
                    right: '15px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none'
                }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-down" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                    </svg>
                </div>
            </div>
        );
    };

    return (
        <>
            {/* <ToastContainer /> */}
            <Modal show={show} onHide={handleClose} centered className="credential-page" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold text-dark" style={{ fontSize: "24px" }}>Create New Engagement</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Formik
                        initialValues={{
                            clientId: clientId && /^[0-9a-fA-F]{24}$/.test(clientId) ? clientId : "",
                            engagementType: "",
                            engagementYear: "",
                            materiality: "",
                            expectations: "",
                        }}
                        enableReinitialize={true}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting }) => (
                            <FormikForm>
                                <Form.Group controlId="clientId">
                                    <Form.Label>Client*</Form.Label>
                                        <Field 
                                            name="clientId" 
                                            component={CustomSelect}
                                        >
                                            <option value="">Select Client</option>
                                            {clients.map(client => (
                                                <option 
                                                    key={client._id || client.clientId} 
                                                    value={client._id || client.clientId}
                                                >
                                                    {client.clientName}
                                                </option>
                                            ))}
                                        </Field>
                                    
                                    <ErrorMessage name="clientId" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group controlId="engagementType" className="mt-3">
                                    <Form.Label>Engagement Type*</Form.Label>
                                    <Field 
                                        name="engagementType" 
                                        component={CustomSelect}
                                    >
                                        <option value="">Select Type</option>
                                        <option value="Audit">Audit</option>
                                        <option value="Review">Review</option>
                                    </Field>
                                    <ErrorMessage name="engagementType" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group controlId="engagementYear" className="mt-3">
                                    <Form.Label>Engagement Year*</Form.Label>
                                    <Field 
                                        name="engagementYear" 
                                        component={CustomSelect}
                                    >
                                        <option value="">Select Year</option>
                                        {engagementYears.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="engagementYear" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group controlId="materiality" className="mt-3">
                                    <Form.Label>Materiality($)*</Form.Label>
                                    <FormattedNumberInput 
                                        id="materiality"
                                        name="materiality" 
                                        placeholder="Enter Amount" 
                                    />
                                    <ErrorMessage name="materiality" component="div" className="text-danger" />
                                </Form.Group>

                                <Form.Group controlId="expectations" className="mt-3">
                                    <Form.Label>Expectations</Form.Label>
                                    <Field 
                                        as="textarea" 
                                        name="expectations" 
                                        className="form-control p-3" 
                                        rows={3} 
                                        placeholder="Enter Here" 
                                        style={{ backgroundColor: "#E7EFF8", borderRadius: '8px' }} 
                                    />
                                </Form.Group>
                                <Modal.Footer>
                                    <Button variant="outline-primary" className="px-5 py-3" onClick={handleClose}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" className="text-white font-16 px-5 py-3" type="submit" disabled={isSubmitting || loading}>
                                        {isSubmitting || loading ? "Submitting..." : "Continue"}
                                    </Button>
                                </Modal.Footer>
                            </FormikForm>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default EngagementModal;